import { msg } from 'gt-react';
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { SquaresFourIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { ProductModule } from '@erp/shared-types';
import { waitForFeatureFlags } from 'src/integrations/feature-flags/feature-flags';
import {
  Route as pathlessAuthenticatedRoute,
  requireAnyPermission,
  requireFeatureFlag,
  requireModules,
} from '../../route';
import { CashDashboardSearchSchema } from './cashDashboardSearch';

const ARCashDashboardPage = lazyRouteComponent(() =>
  import('./ARCashDashboardPage').then((module) => ({
    default: module.ARCashDashboardPage,
  })),
);

/**
 * The customizable Cash Dashboard: the cash metrics template as a per-user
 * canvas. Needs the AR module, AR payment read and the
 * `ar-cash-dashboard-enabled` flag; otherwise the nav item is hidden and a
 * direct URL is not found. The static page stays at /cash-metrics.
 */
export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/cash-dashboard',
  staticData: {
    app: {
      nav: {
        label: msg('Cash Dashboard', {
          context: 'sidebar nav label, Accounts Receivable section',
        }),
        icon: <SquaresFourIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 13,
      },
      access: {
        featureFlag: 'ar-cash-dashboard-enabled',
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARCash }],
      },
    },
  },
  component: ARCashDashboardPage,
  loader: () => ({
    crumb: msg('Cash Dashboard', { context: 'page breadcrumb' }),
  }),
  validateSearch: CashDashboardSearchSchema,
  beforeLoad: async ({ context }) => {
    // On a cold load PostHog may not have returned flags yet.
    await waitForFeatureFlags();
    requireFeatureFlag('ar-cash-dashboard-enabled');
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requireAnyPermission({
      context,
      permissions: [{ action: Action.Read, subject: Subject.ARCash }],
    });
  },
});
